import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * ProxyViewer — Opens a WebSocket to the relay and renders the proxied
 * HTTP response from a remote device. The relay pipes browser↔agent traffic,
 * and the agent proxies to the local device.
 *
 * URL params:
 *   ws    — relay WebSocket URL
 *   ip    — target device IP (display only)
 *   port  — target device port (display only)
 *   session — session ID
 */
export default function ProxyViewer() {
  const [params] = useSearchParams()
  const wsUrl = params.get('ws') || ''
  const ip = params.get('ip') || ''
  const port = params.get('port') || ''
  const sessionId = params.get('session') || ''

  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting')
  const [responseData, setResponseData] = useState<string>('')
  const [contentType, setContentType] = useState<string>('')
  const [errorDetail, setErrorDetail] = useState<string>('')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const chunksRef = useRef<Uint8Array[]>([])

  useEffect(() => {
    if (!wsUrl) {
      setStatus('error')
      setErrorDetail('Missing WebSocket URL. Please start a new session from the site page.')
      return
    }

    // Validate URL format
    if (!wsUrl.startsWith('ws://') && !wsUrl.startsWith('wss://')) {
      setStatus('error')
      setErrorDetail(`Invalid WebSocket URL format: ${wsUrl.slice(0, 50)}...`)
      return
    }

    setStatus('connecting')
    setErrorDetail('')
    chunksRef.current = []
    setResponseData('')
    setContentType('')

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws
    ws.binaryType = 'arraybuffer'

    ws.onopen = () => {
      setStatus('connected')
      // The relay already told the agent to CONNECT when the Worker called /command.
      // Once the browser WS connects to the relay, the relay bridges frames.
      // Send an initial HTTP GET request through the tunnel.
      const scheme = port === '443' || port === '8443' ? 'https' : 'http'
      const httpReq = `GET / HTTP/1.1\r\nHost: ${ip}\r\nConnection: close\r\nAccept: text/html,application/xhtml+xml,*/*\r\nUser-Agent: AmberProxy/1.0\r\n\r\n`
      const encoded = new TextEncoder().encode(httpReq)
      ws.send(encoded.buffer)
    }

    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        const data = new Uint8Array(event.data)
        chunksRef.current.push(data)

        // Try to detect content-type from the first chunk (HTTP response headers)
        if (!contentType && chunksRef.current.length <= 3) {
          const text = new TextDecoder().decode(data)
          const ctMatch = text.match(/content-type:\s*([^\r\n]+)/i)
          if (ctMatch) {
            setContentType(ctMatch[1].trim())
          }
        }

        // Accumulate all data and render
        const allData = concatenateChunks(chunksRef.current)
        const decoded = new TextDecoder().decode(allData)

        // Split headers from body
        const headerEnd = decoded.indexOf('\r\n\r\n')
        if (headerEnd >= 0) {
          const body = decoded.slice(headerEnd + 4)
          setResponseData(body)
        } else {
          setResponseData(decoded)
        }
      } else if (typeof event.data === 'string') {
        // JSON control message from relay
        try {
          const msg = JSON.parse(event.data)
          if (msg.type === 'connected' && msg.payload) {
            if (!msg.payload.ok) {
              setStatus('error')
              setResponseData(`Connection failed: ${msg.payload.error || 'Unknown error'}`)
            }
          }
        } catch { /* not JSON — append as text */ }
      }
    }

    ws.onclose = () => {
      setStatus('disconnected')
      // Render final accumulated data into iframe
      renderToIframe()
    }

    ws.onerror = () => {
      setStatus('error')
      setErrorDetail('WebSocket connection failed. The relay may be unreachable.')
    }

    return () => {
      ws.close()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsUrl])

  const renderToIframe = () => {
    if (iframeRef.current && responseData) {
      const doc = iframeRef.current.contentDocument
      if (doc) {
        doc.open()
        doc.write(responseData)
        doc.close()
      }
    }
  }

  // Render into iframe whenever responseData updates
  useEffect(() => {
    if (responseData && iframeRef.current) {
      const doc = iframeRef.current.contentDocument
      if (doc) {
        doc.open()
        doc.write(responseData)
        doc.close()
      }
    }
  }, [responseData])

  const disconnect = () => {
    if (wsRef.current) {
      wsRef.current.close()
    }
    window.close()
  }

  const statusColors: Record<string, string> = {
    connecting: 'var(--accent)',
    connected: 'var(--ok)',
    disconnected: 'var(--text-3)',
    error: 'var(--err)',
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-0)' }}>
      {/* Header bar */}
      <div style={{
        height: 44, background: 'var(--bg-1)', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%',
            background: statusColors[status],
          }} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--text-1)' }}>
            {ip}:{port}
          </span>
          <span style={{
            fontSize: 11, color: 'var(--text-3)', padding: '2px 8px',
            borderRadius: 'var(--r-f)', background: 'var(--bg-2)',
            textTransform: 'capitalize',
          }}>
            {status}
          </span>
          {contentType && (
            <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
              {contentType}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
            {sessionId.slice(0, 8)}
          </span>
          <button
            onClick={disconnect}
            style={{
              padding: '4px 12px', borderRadius: 'var(--r-m)',
              background: 'var(--err)', color: '#fff', border: 'none',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Disconnect
          </button>
        </div>
      </div>

      {/* Content area */}
      {status === 'error' ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12,
        }}>
          <div style={{ fontSize: 48, opacity: 0.3 }}>
            <svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <circle cx="12" cy="12" r="10"/><path d="m15 9-6 6M9 9l6 6"/>
            </svg>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)', textAlign: 'center', maxWidth: 420 }}>
            {errorDetail || responseData || 'Failed to connect to device'}
          </div>
          {wsUrl && (
            <button
              onClick={() => window.location.reload()}
              style={{
                marginTop: 8, padding: '8px 20px', borderRadius: 'var(--r-m)',
                background: 'var(--accent)', color: '#000', border: 'none',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Retry Connection
            </button>
          )}
        </div>
      ) : status === 'connecting' ? (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, border: '3px solid var(--border)',
            borderTopColor: 'var(--accent)', borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <div style={{ fontSize: 13, color: 'var(--text-3)' }}>
            Connecting to {ip}:{port} via relay...
          </div>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          title={`Proxy: ${ip}:${port}`}
          style={{
            flex: 1, border: 'none', background: '#fff',
            width: '100%',
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      )}
    </div>
  )
}

function concatenateChunks(chunks: Uint8Array[]): Uint8Array {
  const totalLen = chunks.reduce((acc, c) => acc + c.length, 0)
  const result = new Uint8Array(totalLen)
  let offset = 0
  for (const chunk of chunks) {
    result.set(chunk, offset)
    offset += chunk.length
  }
  return result
}
